import { APPROVAL_ACCOUNT_NETWORK_FIELD_DEFAULTS } from '../stores/accounts/approvalFormStore'
import {
	isAccountEksWorkload,
	withEksPodSecondaryCidr
} from './accountNetworkValidation'
import {
	accountHasNetworkRequestData,
	accountRecordShowsIpAllocationAzC,
	defaultVpcAvailabilityZonesForAccountType,
	pickAccountNetworkFieldsFromDetails
} from './accountRequestNetworkSummary'
import { buildDefaultPrivateHostedZoneName, buildDefaultVpcName, resolveTagScalar } from './accountVpcName'
import { normalizeIpAllocationsFromForm } from './ipAllocationValidation'

export type BuildNetworkApprovalFormValuesOptions = {
	/** When true, only merge stored network fields if the source already has network data. */
	onlyPrefillWhenNetworkPresent?: boolean
	systemAlias?: unknown
	distributionList?: string
	/** STG network approval always uses env segment `st`. */
	isStgFlow?: boolean
}

/** Build form state for account / STG network approval editors. */
export function buildNetworkApprovalFormValues(
	sourceDetails: Record<string, unknown>,
	accountType: string | undefined,
	options?: BuildNetworkApprovalFormValuesOptions
): Record<string, unknown> {
	const hasNetworkRequest = accountHasNetworkRequestData(sourceDetails)
	const onlyWhenPresent = options?.onlyPrefillWhenNetworkPresent ?? true
	const shouldPrefillStoredNetwork = hasNetworkRequest || !onlyWhenPresent
	const picked = shouldPrefillStoredNetwork
		? pickAccountNetworkFieldsFromDetails(sourceDetails)
		: {}

	const isProduction = accountRecordShowsIpAllocationAzC(accountType)
	const accountWt = String(sourceDetails.accountWorkloadType ?? 'Non-EKS')
	const ipRequired = isAccountEksWorkload(accountWt)
	const normalizedIp = normalizeIpAllocationsFromForm(
		sourceDetails.ip_address_allocations,
		isProduction,
		ipRequired
	)

	const merged: Record<string, unknown> = {
		...APPROVAL_ACCOUNT_NETWORK_FIELD_DEFAULTS,
		...picked,
		addSecondaryCidrBlock:
			shouldPrefillStoredNetwork &&
			(sourceDetails.addSecondaryCidrBlock === true ||
				String(sourceDetails.addSecondaryCidrBlock ?? '').toLowerCase() === 'true'),
		secondaryCidrBlock: shouldPrefillStoredNetwork
			? String(picked.secondaryCidrBlock ?? sourceDetails.secondaryCidrBlock ?? '')
			: '',
		addPublicCidrBlock:
			shouldPrefillStoredNetwork &&
			(sourceDetails.addPublicCidrBlock === true ||
				String(sourceDetails.addPublicCidrBlock ?? '').toLowerCase() === 'true'),
		publicCidrBlock: shouldPrefillStoredNetwork
			? String(picked.publicCidrBlock ?? sourceDetails.publicCidrBlock ?? '')
			: '',
		accountWorkloadType: accountWt,
		ip_address_allocations: normalizedIp.map(({ id, label, cidrAzA, cidrAzB, cidrAzC }) =>
			isProduction
				? { id, label, cidrAzA, cidrAzB, cidrAzC: cidrAzC ?? '' }
				: { id, label, cidrAzA, cidrAzB }
		)
	}

	// EKS accounts need the custom-CNI pod range as a secondary CIDR; default it in and tick the
	// checkbox, since the payload drops secondaryCidrBlock when addSecondaryCidrBlock is false.
	if (ipRequired) {
		merged.secondaryCidrBlock = withEksPodSecondaryCidr(merged.secondaryCidrBlock)
		merged.addSecondaryCidrBlock = true
	}

	if (options?.systemAlias !== undefined) {
		merged.platformSubgroupName = resolveTagScalar(options.systemAlias)
	}
	if (!String(merged.vpcAvailabilityZones ?? '').trim()) {
		merged.vpcAvailabilityZones = defaultVpcAvailabilityZonesForAccountType(accountType)
	}
	if (!String(merged.vpcName ?? '').trim()) {
		const vpcName = buildDefaultVpcName({
			systemAlias: options?.systemAlias,
			distributionList: options?.distributionList,
			isStgFlow: options?.isStgFlow,
			accountType
		})
		if (vpcName) {
			merged.vpcName = vpcName
		}
	}
	if (!String(merged.privateHostedZoneName ?? '').trim()) {
		const privateHostedZoneName = buildDefaultPrivateHostedZoneName({
			systemAlias: options?.systemAlias,
			isStgFlow: options?.isStgFlow,
			accountType
		})
		if (privateHostedZoneName) {
			merged.privateHostedZoneName = privateHostedZoneName
		}
	}

	return merged
}
